/**
 * One clip, played on demand: a *Listen* button that fetches the audio with the operator's
 * token (one `file.read` row per fetch) and becomes the player once it is here. The identity
 * page's audio rows, the identity's Voices card, the Unassigned voices tab and the graph's Voice
 * card all play through this one piece. A fetch that fails says why in words — the failure
 * text is the caller's, since a voice clip and a whole recording fail for different reasons —
 * and offers to try again only where trying again can help.
 */
import { Play } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RecordingPlayer } from '@/components/humint/questioning/RecordingPlayer'
import { Button } from '@/components/ui/button'
import { useAudioPlayback } from '@/features/humint/recording/useAudioPlayback'
import { useRecordingAudio } from '@/features/humint/recording/useRecordingAudio'

const COPY = 'identity.detail.audio'

/** What the player says of a fetch that failed; `retry` false when trying again cannot help. */
export interface AudioFailureText {
  text: string
  retry: boolean
}

export interface AudioPlaybackProps {
  /** The `/api/v1/…` path of the audio. */
  url: string
  /** A file name whose suffix names the audio type when the URL does not (`clip.wav`). */
  typedAs?: string
  /** The failure in words; left out, the generic sentence with a retry. */
  failureText?: (error: unknown) => AudioFailureText
  /** The listen button's size; the player itself keeps its own. */
  size?: 'sm' | 'xs'
}

export function AudioPlayback({ url, typedAs, failureText, size = 'sm' }: AudioPlaybackProps) {
  const { t } = useTranslation()
  const [requested, setRequested] = useState(false)
  const [attempt, setAttempt] = useState(0)
  if (requested)
    return (
      <AudioRequest
        key={attempt}
        url={url}
        typedAs={typedAs}
        failureText={failureText}
        onRetry={() => setAttempt(value => value + 1)}
      />
    )
  return (
    <Button variant="outline" size={size} onClick={() => setRequested(true)}>
      <Play aria-hidden className="size-4" />
      {t(`${COPY}.listen`)}
    </Button>
  )
}

function AudioRequest({
  url,
  typedAs,
  failureText,
  onRetry,
}: Pick<AudioPlaybackProps, 'url' | 'typedAs' | 'failureText'> & { onRetry: () => void }) {
  const { t } = useTranslation()
  const audio = useRecordingAudio(url, typedAs)
  if (audio.status === 'failed') {
    const failure = failureText?.(audio.error) ?? { text: t(`${COPY}.playFailed`), retry: true }
    return (
      <div className="space-y-2">
        <p role="alert" className="text-sm text-destructive">
          {failure.text}
        </p>
        {failure.retry && (
          <Button variant="outline" size="sm" onClick={onRetry}>
            {t('humint.common.actions.retry')}
          </Button>
        )}
      </div>
    )
  }
  if (audio.status === 'ready') return <LoadedPlayer src={audio.src} />
  return (
    <Button variant="outline" size="sm" loading>
      {t(`${COPY}.listen`)}
    </Button>
  )
}

function LoadedPlayer({ src }: { src: string }) {
  const player = useAudioPlayback(src, 0)
  return (
    <div className="w-full">
      <RecordingPlayer
        audio={player.failed ? 'unavailable' : 'ready'}
        playback={player.playback}
        waveform={[]}
        actions={{ onTogglePlay: player.toggle, onSeek: player.seek }}
      />
    </div>
  )
}
